import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Zap, Loader2, User, Gift, CheckCircle2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AuthModal from "./AuthModal";
import { trackEvent } from "@/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import ConfettiCelebration from "./ConfettiCelebration";

interface RefillModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCredits: number;
}

const RefillModal = ({ open, onOpenChange, currentCredits }: RefillModalProps) => {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const queryClient = useQueryClient();
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<{ credits: number } | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleRefill = async () => {
    if (!user || !session) {
      setShowAuthModal(true);
      return;
    }

    setLoading(true);
    trackEvent("checkout_started", { plan: "refill" });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { type: "refill" },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setPromoLoading(true);
    setPromoError(null);
    setPromoSuccess(null);

    try {
      const { data, error } = await supabase.rpc("redeem_promo_code", {
        input_code: promoCode.trim(),
      } as any);

      if (error) throw error;

      const result = data as any;
      if (!result?.success) {
        setPromoError(result?.error ?? "Failed to redeem code");
        return;
      }

      // Success
      setPromoSuccess({ credits: result.credits_added });
      setShowConfetti(true);
      trackEvent("promo_code_redeemed", { code: promoCode.trim(), credits: result.credits_added });

      // Refresh credits
      queryClient.invalidateQueries({ queryKey: ["shield-credits", user.id] });

      toast.success(`🎉 +${result.credits_added} credits added!`, {
        description: `Your balance is now ${result.new_credits} credits.`,
        duration: 5000,
      });

      // Auto-close after celebration
      setTimeout(() => {
        setPromoCode("");
        setPromoSuccess(null);
        onOpenChange(false);
      }, 2500);
    } catch (err: any) {
      console.error("Promo redeem error:", err);
      setPromoError(err?.message ?? "Something went wrong");
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <>
      <ConfettiCelebration trigger={showConfetti} onComplete={() => setShowConfetti(false)} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-md text-center overflow-y-auto max-h-[90vh]">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-cyan/20 flex items-center justify-center">
                <Shield className="w-8 h-8 text-cyan" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground">
              Credits Depleted
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <p className="text-muted-foreground">
              You've used all your credits. Add credits to continue analyzing messages and generating content.
            </p>

            <div className="space-y-3 text-left">
              <div className="flex items-center gap-3 text-foreground">
                <Zap className="w-5 h-5 text-cyan" />
                <span>10 credits</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <Shield className="w-5 h-5 text-cyan" />
                <span>Use for analysis, strategy, and content</span>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-3xl font-bold text-foreground mb-1">
                $4.99 <span className="text-sm font-normal text-muted-foreground">one-time</span>
              </p>
              <p className="text-sm text-muted-foreground mb-4">10 credits</p>
              
              {!user && (
                <p className="text-xs text-muted-foreground mb-3 flex items-center justify-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  Sign in required to purchase
                </p>
              )}
              
              <button
                onClick={handleRefill}
                disabled={loading}
                className={cn(
                  "btn-glow block w-full py-4 rounded-xl font-bold text-lg tracking-wide",
                  "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                  "transition-all duration-300 ease-out",
                  "hover:scale-[1.02] active:scale-[0.98]",
                  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                  "text-center"
                )}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading...
                  </span>
                ) : user ? (
                  "Buy 10 Credits — $4.99"
                ) : (
                  "Sign In to Buy Credits"
                )}
              </button>
            </div>

            {/* Promo Code Section */}
            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 justify-center mb-3">
                <Gift className="w-4 h-4 text-cyan" />
                <span className="text-sm font-semibold text-foreground">Have a promo code?</span>
              </div>

              {promoSuccess ? (
                <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 animate-scale-in">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-400">
                    +{promoSuccess.credits} credits added!
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoError(null);
                      }}
                      placeholder="ENTER CODE"
                      className={cn(
                        "flex-1 bg-secondary border rounded-lg px-3 py-2.5 text-sm font-mono text-foreground placeholder:text-muted-foreground",
                        "focus:outline-none focus:ring-2 focus:ring-cyan/40 transition-all",
                        promoError ? "border-destructive/50" : "border-border"
                      )}
                      onKeyDown={(e) => e.key === "Enter" && handleRedeemCode()}
                    />
                    <button
                      onClick={handleRedeemCode}
                      disabled={promoLoading || !promoCode.trim()}
                      className={cn(
                        "px-4 py-2.5 rounded-lg text-sm font-semibold transition-all",
                        "bg-cyan/10 text-cyan hover:bg-cyan/20",
                        "disabled:opacity-50 disabled:cursor-not-allowed"
                      )}
                    >
                      {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}
                    </button>
                  </div>
                  {promoError && (
                    <div className="flex items-center gap-1.5 text-xs text-destructive animate-fade-in">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      {promoError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </>
  );
};

export default RefillModal;
